// IfsProcessingModel.js
import AWS from 'aws-sdk';
import axios from "axios";

AWS.config.update({
  region: process.env.REACT_APP_UAT_AWS_REGION,
  credentials: {
    accessKeyId: process.env.REACT_APP_UAT_AWS_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_UAT_AWS_SECRET_ACCESS_KEY
  }
});

const selectedTool = "IFS";
const baseFolderPath = 'ifs/';
const apiEndpoint = 'ifs';

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const timeout = setTimeout(() => {
      reader.abort();
      reject(new Error("File reading timed out. The file may be too large."));
    }, 60000);

    reader.onload = () => {
      clearTimeout(timeout);
      try {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      } catch (error) {
        reject(new Error("Failed to process file as base64"));
      }
    };

    reader.onerror = error => {
      clearTimeout(timeout);
      reject(new Error("Failed to read file"));
    };

    reader.readAsDataURL(file);
  });
};

export const getAllLanguages = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error("Authentication token not found. Please log in again.");
    }

    const response = await axios.get(
      `https://devai.conasservices.com/v1/uat_ifs/${apiEndpoint}/dropdown`,
      {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': token
        }
      }
    );

    if (response.status === 200 && response.data && response.data.customers) {
      return response.data.customers.map(lang => ({
        id: lang.id,
        name: lang.language_name
      }));
    }

    return [];
  } catch (error) {
    console.error("Error fetching languages:", error);
    throw error;
  }
};

export const uploadAutomationData = async (selectedFiles, selectedTool, selectedModel, uniqueRecords, totalCount, folderId) => {
  try {
    if (!selectedFiles || selectedFiles.length === 0) {
      throw new Error("No files selected");
    }

    const uploadResults = [];
    let apiResponse;  // ← declared outside loop so it's accessible in return

    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error("Authentication token not found. Please log in again.");
    }

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];

      const fileExtension = file.name.split('.').pop();

      let base64String;
      try {
        base64String = await fileToBase64(file);
      } catch (error) {
        throw new Error(`Failed to convert file to base64: ${error.message}`);
      }

      const payload = {
        fileName: file.name,
        base64File: base64String,
        custom_batch_id: folderId,
        tool_name: selectedTool,
        model_name: selectedModel,
        unique_records: uniqueRecords,
        total_records: totalCount,
      };

      const apiUrl = `https://devai.conasservices.com/v1/uat_ifs/${apiEndpoint}/input_file`;

      try {
        apiResponse = await axios.post(  // ← no `let`, just assign to outer variable
          apiUrl,
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': token
            },
            timeout: 120000
          }
        );
      } catch (error) {
        if (error.code === 'ECONNABORTED') {
          throw new Error("API request timed out. The server may be busy, please try again later.");
        }
        throw error;
      }

      if (apiResponse && apiResponse.status === 201) {
        const key = `${baseFolderPath}${folderId}/input_file.${fileExtension}`;

        const s3 = new AWS.S3();

        const uploadParams = {
          Bucket: process.env.REACT_APP_UAT_AWS_BUCKET,
          Key: key,
          ContentType: file.type,
          Expires: 300
        };

        let presignedUrl;
        try {
          presignedUrl = await new Promise((resolve, reject) => {
            s3.getSignedUrl('putObject', uploadParams, (err, url) => {
              if (err) {
                reject(err);
                return;
              }
              resolve(url);
            });
          });
        } catch (error) {
          throw new Error(`S3 pre-signed URL generation failed: ${error.message}`);
        }

        try {
          const uploadResponse = await fetch(presignedUrl, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type
            }
          });

          if (!uploadResponse.ok) {
            throw new Error(`S3 upload failed with status: ${uploadResponse.status}`);
          }
        } catch (error) {
          throw new Error(`Failed to upload file to S3: ${error.message}`);
        }

        const s3Url = `https://${uploadParams.Bucket}.s3.amazonaws.com/${key}`;

        uploadResults.push({
          originalFile: file.name,
          fileSize: file.size,
          fileType: file.type,
          s3Location: s3Url,
          key: key,
          folderId: folderId,
          activityType: selectedTool
        });
      } else {
        throw new Error(`File validation failed. API returned status ${apiResponse ? apiResponse.status : 'unknown'}`);
      }
    }

    return {
      status: 201,
      data: uploadResults,
      apiResponse: apiResponse.data  // ← now accessible, contains duplicate_check
    };

  } catch (error) {
    throw error;
  }
};

export const processAutomationData = async (
  selectedTool, data, selectedModel,
  totalCount, batch_id,
  limit = 100,
  offset = 0
) => {
  try {
    const token = localStorage.getItem('token');
    const payload = {
      ...data,
      model_name: selectedModel,
      reference_batch_id: batch_id,
      limit,
      offset,
    };

    const response = await axios.post(
      `https://devai.conasservices.com/v1/uat_ifs/ifs/process_records`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': token
        },
        timeout: 28000
      }
    );

    return response.data;
  } catch (error) {
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw error;
  }
};

export const checkAndDeleteFileFromS3 = async (fileModelResponse, folderId, data, baseFolderPath = '', selectedTool = '') => {
  try {
    if (fileModelResponse.input_count !== fileModelResponse.output_count) {
      return {
        status: 'info',
        message: 'File not deleted: counts do not match',
        details: {
          input: fileModelResponse.input_count,
          output: fileModelResponse.output_count
        }
      };
    }

    if (!folderId) {
      return { status: 'error', message: 'Missing required folderId parameter' };
    }

    let fileExtension;
    if (selectedTool) {
      fileExtension = selectedTool.split('.').pop();
    } else if (data?.data?.[0]?.originalFile) {
      fileExtension = data.data[0].originalFile.split('.').pop();
    } else {
      fileExtension = 'xlsx';
    }

    if (!baseFolderPath && data?.data?.[0]?.activityType) {
      baseFolderPath = 'ifs/';
    }

    const key = baseFolderPath
      ? `${baseFolderPath}${folderId}/input_file.${fileExtension}`
      : `${folderId}/input_file.${fileExtension}`;

    const s3 = new AWS.S3();

    const deleteParams = {
      Bucket: process.env.REACT_APP_UAT_AWS_BUCKET,
      Key: key
    };

    return new Promise((resolve, reject) => {
      s3.deleteObject(deleteParams, (err, deleteData) => {
        if (err) {
          reject({ status: 'error', message: 'Failed to delete file from S3', error: err });
        } else {
          resolve({ status: 'success', message: 'File deleted successfully from S3' });
        }
      });
    });
  } catch (error) {
    return {
      status: 'error',
      message: 'Error while checking and deleting file',
      error: error.toString()
    };
  }
};

export const checkCountData = async (data, selectedTool, selectedModel) => {
  try {
    const token = localStorage.getItem('token');

    const payload = {
      ...data,
      model_name: selectedModel
    };

    const response = await axios.post(
      `https://devai.conasservices.com/v1/uat_ifs/${apiEndpoint}/input_check`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': token
        }
      }
    );

    if (response === undefined) {
      throw new Error("No response");
    }

    if (response.status >= 200 && response.status <= 204) {
      if (response.data?.status === 204) {
        throw new Error(response.data.message);
      } else {
        return response.data;
      }
    }
  } catch (error) {
    let sendError = error;
    if (error.response?.data?.message) {
      sendError = new Error(error.response.data.message);
    } else if (error.response?.data?.detail) {
      sendError = new Error(error.response.data.detail);
    } else {
      sendError = new Error(
        "Something unexpected happened. " +
        "If the issue persists, please contact our support team for assistance."
      );
    }
    throw sendError;
  }
};

export const translateRecords = async (batchId, language, maxConcurrent = 10, onProgress) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error("Authentication token not found. Please log in again.");
    }

    const payload = {
      batch_id: batchId,
      language: language.toLowerCase(),
      max_concurrent: maxConcurrent,
      model: "gemini-1.5-flash"
    };

    const response = await fetch(
      `https://devai.conasservices.com/v1/uat_ifs/${apiEndpoint}/translate_records`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': token
        },
        body: JSON.stringify(payload)
      }
    );

    if (!response.ok) {
      throw new Error(`Translation request failed with status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n').filter(line => line.trim());

      for (const line of lines) {
        try {
          const data = JSON.parse(line);
          if (onProgress) {
            onProgress(data);
          }
        } catch (e) {
          console.error('Error parsing JSON line:', e);
        }
      }
    }

    return { success: true };
  } catch (error) {
    throw error;
  }
};

export const downloadProcessedData = async (
  selectedTool, batchId, model, isTranslated = false
) => {
  if (!batchId) throw new Error('Batch ID is required.');

  const apiKey = localStorage.getItem('token');
  if (!apiKey) throw new Error('API key not found. Please log in again.');

  const url = `https://devai.conasservices.com/v1/uat_ifs/ifs/output_file`
    + `?batch_id=${encodeURIComponent(batchId)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey
    }
  });

  if (!response.ok) {
    throw new Error(`Server responded with status: ${response.status}`);
  }

  const { download_url, filename } = await response.json();

  const fileResponse = await fetch(download_url);

  if (!fileResponse.ok) {
    throw new Error(`S3 download failed: ${fileResponse.status}`);
  }

  const blob = await fileResponse.blob();

  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(objectUrl), 100);

  return { filename };
};

export const gethistoryData = async (selectedTool) => {
  try {
    const token = localStorage.getItem('token');

    const response = await axios.get(
      `https://devai.conasservices.com/v1/uat_ifs/${apiEndpoint}/operation_history`,
      {
        params: {
          tool_name: selectedTool
        },
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': token
        }
      }
    );

    if (response === undefined) {
      throw new Error("No response");
    }

    if (response.status >= 200 && response.status <= 204) {
      if (response.data?.status === 204) {
        throw new Error(response.data.message);
      } else {
        return response.data;
      }
    }
  } catch (error) {
    let sendError = error;
    if (error.response?.data?.message) {
      sendError = new Error(error.response.data.message);
    } else if (error.response?.data?.detail) {
      sendError = new Error(error.response.data.detail);
    } else {
      sendError = new Error(
        "Something unexpected happened. " +
        "If the issue persists, please contact our support team for assistance."
      );
    }
    throw sendError;
  }
};

export const downloadInputFile = async (selectedTool, batchId) => {
  try {
    if (!batchId) {
      throw new Error('Batch ID is required for downloading input file.');
    }

    const apiKey = localStorage.getItem('token');
    if (!apiKey) {
      throw new Error('API key not found in local storage. Please log in again.');
    }

    const url = `https://devai.conasservices.com/v1/uat_ifs/${apiEndpoint}/input_download?batch_id=${encodeURIComponent(batchId)}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey
      }
    });

    if (!response.ok) {
      throw new Error(`Server responded with status: ${response.status}`);
    }

    const base64Data = await response.text();

    return { base64Data };
  } catch (error) {
    throw error;
  }
};